/*
  Southern Oaks accessibility patches (Movy Media, 2026-10-01). Implements the theme-side fixes from the
  WCAG 2.1 AA review of October 1, 2026 (client folder: WCAG-2.1-REVIEW-2026-10-01.md) for things PageFly
  cannot set itself. Styles: southern-oaks-globals.css section 11.
  - Decorative images: PageFly images with no alt, or a file name as alt, get alt="" so screen readers skip
    them. Real photo alts are set per image in PageFly and are untouched. (Moved here from theme.liquid.)
  - Text contrast (1.4.3): PageFly text set in Taupe #8C7A63, the light eyebrow grays, or Chocolate at
    50 percent is drawn in Deep Taupe #665847. Only the color property of PageFly element rules changes;
    borders, lines and backgrounds keep Taupe.
  - Background video (2.2.2): a pause and play button on every autoplaying PageFly video. Starts paused
    for reduced motion. A visitor's pause sticks, even when the video scrolls back into view.
  - Forms (1.3.5, 3.3.2): autofill hints for name and email, and an accessible name for fields with no label.
  - Product metafield lists (1.3.1): PageFly lists that hold text but no items are read as plain text.
  - Product image sliders (2.1.1): scrollable image strips can take keyboard focus.
  The script is deferred, so it runs as soon as it loads (the page is parsed), again on DOMContentLoaded
  and again on window load. Every step is safe to repeat.
*/
(function () {
  var DEEP_TAUPE = '#665847';
  // Taupe, the eyebrow grays, and Chocolate at 50 percent (renders as #9E9891 on Ivory, King Street eyebrows)
  var LOW_CONTRAST = /^(rgb\(\s*140,\s*122,\s*99\s*\)|rgb\(\s*153,\s*146,\s*137\s*\)|rgb\(\s*158,\s*152,\s*145\s*\)|rgba\(\s*61,\s*50,\s*40,\s*0?\.5\s*\)|#8c7a63|#999289|#9e9891)$/i;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Decorative images ---------- */
  function fixImages() {
    document.querySelectorAll('.__pf img').forEach(function (img) {
      var alt = img.getAttribute('alt');
      if (alt === null || /__PID:|\.(svg|png|jpe?g|webp)$/i.test(alt)) img.setAttribute('alt', '');
    });
  }

  /* ---------- Text contrast ---------- */
  function deepen(style) {
    var color = style.getPropertyValue('color');
    if (color && LOW_CONTRAST.test(color.trim())) style.setProperty('color', DEEP_TAUPE, style.getPropertyPriority('color'));
  }
  // PageFly element rules: per-element classes (.pf-57_) and repeated-item classes (.rid-17), always scoped .__pf.__pf_<page>
  function isPageFlyRule(selector) {
    return selector.indexOf('pf-') !== -1 || selector.indexOf('.__pf') !== -1;
  }
  function walkRules(rules) {
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (rule.selectorText && rule.style && isPageFlyRule(rule.selectorText)) deepen(rule.style);
      // Grouping rules (@media, @supports) and nested rules
      if (rule.cssRules && rule.cssRules.length) walkRules(rule.cssRules);
    }
  }
  function fixColors() {
    for (var i = 0; i < document.styleSheets.length; i++) {
      var rules;
      try { rules = document.styleSheets[i].cssRules; } catch (e) { continue; } // cross-origin sheet
      if (rules) walkRules(rules);
    }
    document.querySelectorAll('.__pf [style*="color"]').forEach(function (el) { deepen(el.style); });
  }

  /* ---------- Background video pause and play ---------- */
  var ICON_PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>';
  var ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.2v13.6L19 12z"/></svg>';

  function addVideoToggles() {
    // so-motion.js removes autoplay for reduced motion, so PageFly background videos are matched by type as well
    document.querySelectorAll('.__pf video[autoplay], .__pf video[data-pf-type="BackgroundVideo"]').forEach(function (video) {
      if (video.hasAttribute('data-so-toggle')) return;
      var host = video.closest('[data-pf-type="FlexSection"], [data-pf-type="Section"]') || video.parentElement;
      if (!host) return;
      video.setAttribute('data-so-toggle', '');
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';

      var wantPlay = !reduce;
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'so-video-toggle';

      function render() {
        button.setAttribute('aria-label', wantPlay ? 'Pause background video' : 'Play background video');
        button.innerHTML = wantPlay ? ICON_PAUSE : ICON_PLAY;
        button.classList.toggle('is-paused', !wantPlay);
      }
      button.addEventListener('click', function () {
        wantPlay = !wantPlay;
        if (wantPlay) {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
        render();
      });
      // Keep a visitor's pause: so-motion.js and PageFly both call play() when the video scrolls back into view
      video.addEventListener('play', function () { if (!wantPlay) video.pause(); });

      if (!wantPlay) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      render();
      host.appendChild(button);
    });
  }

  /* ---------- Forms: autofill hints and missing labels ---------- */
  function hasLabel(field) {
    if (field.getAttribute('aria-label') || field.getAttribute('aria-labelledby')) return true;
    var labels = field.labels || [];
    for (var i = 0; i < labels.length; i++) if (labels[i].textContent.trim()) return true;
    return false;
  }
  function fixForms() {
    document.querySelectorAll('.__pf form').forEach(function (form) {
      form.querySelectorAll('input, textarea, select').forEach(function (field) {
        var type = (field.getAttribute('type') || '').toLowerCase();
        if (/^(hidden|submit|button|reset|image)$/.test(type)) return;
        var name = field.getAttribute('name') || '';
        if (!field.hasAttribute('autocomplete') && field.tagName === 'INPUT') {
          if (type === 'email') field.setAttribute('autocomplete', 'email');
          else if ((type === 'text' || type === '') && /name/i.test(name)) {
            // "Business Name" on the wholesale form is the shop, not the person
            field.setAttribute('autocomplete', /business|company|shop|store/i.test(name) ? 'organization' : 'name');
          }
        }
        if (!hasLabel(field)) {
          var text = type === 'email' ? 'Email address' : (field.getAttribute('placeholder') || '').trim();
          if (text) field.setAttribute('aria-label', text);
        }
      });
    });
  }

  /* ---------- Product metafield lists with no items ---------- */
  function fixLists() {
    document.querySelectorAll('.__pf ul[data-pf-type="MetafieldValue"]').forEach(function (ul) {
      if (!ul.querySelector(':scope > li') && !ul.hasAttribute('role')) ul.setAttribute('role', 'presentation');
    });
  }

  /* ---------- Product image sliders ---------- */
  function fixSliders() {
    document.querySelectorAll('.__pf .pf-media-slider').forEach(function (slider) {
      if (slider.hasAttribute('tabindex')) return;
      var scrolls = slider.scrollWidth > slider.clientWidth + 1 || slider.scrollHeight > slider.clientHeight + 1;
      if (!scrolls) return;
      slider.setAttribute('tabindex', '0');
      if (!slider.hasAttribute('role')) slider.setAttribute('role', 'region');
      if (!slider.hasAttribute('aria-label') && !slider.hasAttribute('aria-labelledby')) {
        var box = slider.closest('[data-pf-type="ProductBox"]');
        var title = box && box.querySelector('[data-pf-type="ProductTitle"]');
        var product = title && title.textContent.trim();
        slider.setAttribute('aria-label', product ? 'Product images: ' + product : 'Product images');
      }
    });
  }

  function run() {
    var steps = [fixImages, fixColors, addVideoToggles, fixForms, fixLists, fixSliders];
    for (var i = 0; i < steps.length; i++) {
      try { steps[i](); } catch (e) { /* one failed step never blocks the others */ }
    }
  }

  run();
  document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  // Sliders that only overflow at another screen width (phone to desktop rotation, resized windows)
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fixSliders, 250);
  });
})();
